"use client"

import { useState, useEffect, useRef } from "react"
import { Bar } from "react-chartjs-2"
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend, type ChartOptions, type ChartType } from "chart.js"
import { Box, IconButton, Typography } from "@mui/material"
import { FirstPage, LastPage, NavigateBefore, NavigateNext } from "@mui/icons-material"

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

declare module 'chart.js' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface PluginOptionsByType<TType extends ChartType> {
    verticalBarValueLabel?: {
      showDecimals?: boolean;
      mode?: "cantidad" | "porcentaje";
    };
  }
}

interface VerticalBarChartProps {
  title: string
  data: {
    id: string
    label: string
    value: number
    color?: string
  }[]
  chartType?: "bars" | "lines"
  mode?: "cantidad" | "porcentaje"
  enablePagination?: boolean
  itemsPerPage?: number
  showDecimals?: boolean
}

const verticalBarValueLabelPlugin = {
  id: 'verticalBarValueLabel',
  afterDatasetsDraw(chart: any) {
    const { ctx, data, chartArea } = chart
    const dataset = data.datasets[0]
    const values = dataset.data
    const meta = chart.getDatasetMeta(0)
    if (!meta || !meta.data) return

    const showDecimals = chart.options.plugins?.verticalBarValueLabel?.showDecimals ?? true
    const mode = chart.options.plugins?.verticalBarValueLabel?.mode ?? "cantidad"

    ctx.save()
    ctx.font = 'bold 12px sans-serif'
    ctx.fillStyle = '#000000'
    ctx.textBaseline = 'bottom'
    ctx.textAlign = 'center'

    meta.data.forEach((bar: any, index: number) => {
      const value = values[index]
      if (value === undefined || value === null) return

      const x = bar.x
      const y = bar.y
      const height = bar.height
      const top = y - height / 2

      let formattedValue: string
      if (mode === "porcentaje") {
        const num = showDecimals ? value.toFixed(2) : Math.round(value).toString()
        formattedValue = `${num}%`
      } else {
        formattedValue = showDecimals
          ? value.toLocaleString('en-US', { maximumFractionDigits: 2 })
          : value.toLocaleString('en-US', { maximumFractionDigits: 0 })
      }

      let textX = x
      let textY = top - 5

      if (textY - 6 < chartArea.top) {
        textY = top + height / 2
        ctx.textBaseline = 'middle'
        ctx.fillStyle = '#000000'
      }

      ctx.fillText(formattedValue, textX, textY)
    })

    ctx.restore()
  }
}

export function VerticalBarChart({
  title,
  data,
  mode = "cantidad",
  enablePagination = true,
  itemsPerPage = 10,
  showDecimals = true,
}: VerticalBarChartProps) {
  const [startOffset, setStartOffset] = useState(0)
  const chartRef = useRef<any>(null)

  const totalItems = data.length
  const endOffset = Math.min(startOffset + itemsPerPage, totalItems)
  const paginatedData = data.slice(startOffset, endOffset)
  const showPagination = enablePagination && totalItems > itemsPerPage

  useEffect(() => {
    setStartOffset(0)
  }, [data.length])

  const handleNextItem = () => {
    if (startOffset + itemsPerPage < totalItems) {
      setStartOffset(startOffset + 1)
    }
  }

  const handlePrevItem = () => {
    if (startOffset > 0) {
      setStartOffset(startOffset - 1)
    }
  }

  const handleNext10Items = () => {
    const newOffset = Math.min(startOffset + 10, totalItems - itemsPerPage)
    setStartOffset(Math.max(0, newOffset))
  }

  const handlePrev10Items = () => {
    const newOffset = Math.max(startOffset - 10, 0)
    setStartOffset(newOffset)
  }

  const chartData = {
    labels: paginatedData.map((item) => item.label),
    datasets: [
      {
        data: paginatedData.map((item) => item.value),
        backgroundColor: paginatedData.map((item) => item.color || "rgba(59, 130, 246, 0.7)"),
        borderColor: paginatedData.map((item) => item.color || "rgba(59, 130, 246, 1)"),
        borderWidth: 1,
        barThickness: 40,
        tension: 0.4,
        fill: false,
        borderSkipped: false,
      },
    ],
  }

  const options: ChartOptions<"bar"> = {
    indexAxis: "x",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.parsed.y || 0
            if (mode === "porcentaje") {
              return showDecimals ? `${value?.toFixed(2)}%` : `${Math.round(value)}%`
            }
            return showDecimals
              ? value?.toLocaleString('en-US', { maximumFractionDigits: 2 })
              : value?.toLocaleString('en-US', { maximumFractionDigits: 0 })
          },
        },
      },
      verticalBarValueLabel: {
        showDecimals,
        mode,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { display: true, color: "rgba(0, 0, 0, 0.05)" },
        ticks: {
          callback: (value) => {
            if (mode === "porcentaje") {
              return showDecimals ? `${value}%` : `${Math.round(Number(value))}%`
            }
            return showDecimals
              ? Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 })
              : Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })
          },
        },
      },
      x: { grid: { display: false } },
    },
  }

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.update()
    }
  }, [startOffset, data, mode, showDecimals])

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%" }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: "#2563eb", fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Bar ref={chartRef} data={chartData} options={options} plugins={[verticalBarValueLabelPlugin]} />
      </Box>

      {showPagination && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mt: 2 }}>
          <IconButton size="small" onClick={handlePrev10Items} disabled={startOffset === 0} title="Retroceder 10 items">
            <FirstPage fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handlePrevItem} disabled={startOffset === 0} title="Retroceder 1 item">
            <NavigateBefore fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: "120px", textAlign: "center" }}>
            {startOffset + 1} - {endOffset} de {totalItems}
          </Typography>
          <IconButton
            size="small"
            onClick={handleNextItem}
            disabled={startOffset + itemsPerPage >= totalItems}
            title="Avanzar 1 item"
          >
            <NavigateNext fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleNext10Items}
            disabled={startOffset + itemsPerPage >= totalItems}
            title="Avanzar 10 items"
          >
            <LastPage fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  )
}